import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  Inject,
  PLATFORM_ID,
  OnDestroy,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import {
  LucideAngularModule,
  ArrowLeftIcon,
  MapPinIcon,
  NavigationIcon,
  Loader2Icon,
  BatteryIcon,
  DropletsIcon,
  ZapIcon,
  SettingsIcon,
  PowerIcon,
  DiscIcon,
  SearchIcon,
  CheckIcon,
} from 'lucide-angular';
import { StoreService as StoreDataService } from '../../../services/store.service';
import { Sucursal } from '../../../types/stores';
import {
  EmailValidationService,
  UpdateSucursalPreferidaPayload,
} from '../../../services/email-validation.service';
import { ScrollService } from '../../../services/scroll.service';
import {
  formatDateWithDay,
  formatDistance,
  getWeekDates,
  isContactCenterOpen,
  openInGoogleMaps,
  openInWaze,
} from '../../../utils/stores/store-data';
import { ActivatedRoute } from '@angular/router';
import { getDataFromResolver } from '../../../services/resolvers';
import { Resolvers } from '../../../types/Resolvers';
import { ApiMembresiaResponse } from '../../../types/API.types';
import { DrawerModule } from 'primeng/drawer';
import { PopoverModule } from 'primeng/popover';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'allas-mi-tienda',
  imports: [
    CommonModule,
    LucideAngularModule,
    DrawerModule,
    PopoverModule,
    FormsModule,
  ],
  templateUrl: './mi-tienda.html',
})
export class MiTienda implements OnInit {
  @Input() selectedStoreId?: string;
  @Input() isMobile = false;
  @Input() isFloating = false;
  @Output() onClose = new EventEmitter<void>();
  @Output() onStoreSelect = new EventEmitter<Sucursal>();

  readonly route = inject(ActivatedRoute);

  stores: Sucursal[] =
    getDataFromResolver<ApiMembresiaResponse<Sucursal[]>>(
      Resolvers.STORES,
      this.route
    ).data || [];
  selectedStore: Sucursal | null = null;
  viewStores: Sucursal[] = [];
  location: { lat: number; lng: number } | null = null;
  loading = false;
  error: string | null = null;
  navigationModalVisible = false;
  selectedStoreForNavigation: Sucursal | null = null;
  searchTerm: string = '';
  services: {
    name: string;
    icon: string;
    sizeClass?: string;
  }[] = [];
  cities: { id: string; name: string; stores: string[] }[] = [
    {
      id: 'TODAS',
      name: 'Todas las ciudades',
      stores: [
        '01',
        '04',
        '05',
        '06',
        '07',
        '08',
        '09',
        '11',
        '12',
        '13',
        '14',
        '15',
      ],
    },
    {
      id: 'CHOLOMA',
      name: 'Choloma',
      stores: ['15'],
    },
    {
      id: 'ELPROGRESO',
      name: 'El Progreso',
      stores: ['09'],
    },
    {
      id: 'LACEIBA',
      name: 'La Ceiba',
      stores: ['01'],
    },
    {
      id: 'SANPEDROSULA',
      name: 'San Pedro Sula',
      stores: ['04', '06', '07'],
    },
    {
      id: 'TEGUCIGALPA-COMAYAGUELA',
      name: 'Tegucigalpa-Comayaguela',
      stores: ['05', '08', '11', '12', '13', '14'],
    },
  ];
  selectedCity: string | null = null;

  // Icons
  readonly arrowLeftIcon = ArrowLeftIcon;
  readonly mapPinIcon = MapPinIcon;
  readonly navigationIcon = NavigationIcon;
  readonly loader2Icon = Loader2Icon;
  readonly batteryIcon = BatteryIcon;
  readonly dropletsIcon = DropletsIcon;
  readonly zapIcon = ZapIcon;
  readonly settingsIcon = SettingsIcon;
  readonly powerIcon = PowerIcon;
  readonly discIcon = DiscIcon;
  readonly searchIcon = SearchIcon;
  readonly checkIcon = CheckIcon;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private cdr: ChangeDetectorRef,
    public storeService: StoreDataService,
    private emailValidationService: EmailValidationService,
    private scrollService: ScrollService
  ) {}

  ngOnInit(): void {
    this.selectedCity = null;
    this.setStores(this.stores);
    this.setGeneralServices();
  }

  setStores(stores: Sucursal[]): void {
    this.stores = stores;
    this.viewStores = stores.map((store) => ({
      ...store,
      isOpen: this.determinateIsOpen(),
    }));
  }

  setGeneralServices() {
    this.services = [
      {
        name: `Baterías <br/> Instalación y Diagnóstico`,
        icon: '/allas-icons/battery-blue.svg',
      },
      {
        name: `Cambio de Filtros`,
        icon: '/allas-icons/filters.svg',
      },
      {
        name: `Sistema Eléctrico <br/> Diagnóstico`,
        icon: '/allas-icons/diagnostico-sistema-electrico.svg',
      },
      {
        name: `Garantía Nacional`,
        icon: '/allas-icons/check-guard.svg',
      },
      {
        name: `Reciclaje de Baterías`,
        icon: '/allas-icons/reciclaje-baterias.svg',
      },
      {
        name: `Instalación de Escobillas`,
        icon: '/allas-icons/cambio-limpiaparabrisas-wout-circle.svg',
      },
    ];
  }

  determinateIsOpen(): boolean {
    const hour = (new Date().getUTCHours() - 6 + 24) % 24;

    // Si es entre las 8AM y las 5PM, está abierto
    return hour >= 8 && hour <= 17;
  }

  get filteredStores(): Sucursal[] {
    let stores = this.viewStores;
    const citySelected = this.cities.find(
      (city) => city.id === this.selectedCity
    );

    if (this.searchTerm) {
      stores = stores.filter((store) =>
        store.name.toLowerCase().includes(this.searchTerm.toLowerCase())
      );
    }

    if (citySelected) {
      stores = stores.filter((store) => citySelected.stores.includes(store.id));
    }

    return stores;
  }

  isContactCenterOpen(): boolean {
    return isContactCenterOpen();
  }

  requestLocation(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    this.loading = true;
    this.error = null;

    if (!navigator.geolocation) {
      this.error = 'La geolocalización no está soportada en este navegador';
      this.loading = false;
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.location = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        this.sortStoresByDistance();
        this.loading = false;
        this.cdr.detectChanges();
      },
      (error) => {
        this.error = 'No se pudo obtener la ubicación: ' + error.message;
        this.loading = false;
        this.cdr.detectChanges();
      }
    );
  }

  sortStoresByDistance(): void {
    if (!this.location) return;

    this.viewStores = this.stores
      .map((store) => ({
        ...store,
        distance: this.calculateDistance(
          this.location!.lat,
          this.location!.lng,
          store.coordinates.lat,
          store.coordinates.lng
        ),
      }))
      .sort((a, b) => (a.distance || 0) - (b.distance || 0));
  }

  calculateDistance(
    lat1: number,
    lng1: number,
    lat2: number,
    lng2: number
  ): number {
    const R = 6371; // Earth's radius in kilometers
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  formatDistance(distance: number): string {
    return formatDistance(distance);
  }

  handleStoreClick(store: Sucursal): void {
    this.selectedStore = store;
  }

  handleStoreSelect(store: Sucursal): void {
    // Actualizar la sucursal preferida en el backend
    this.updatePreferredStore(store);

    // Mantener la funcionalidad existente
    this.storeService.setSelectedStore(store);
    this.onStoreSelect.emit(store);
    this.onClose.emit();

    // En mobile, hacer scroll hacia arriba después de seleccionar para evitar desplazamiento visual
    if (this.isMobile) {
      this.scrollService.scrollToTop();
    }
  }

  /**
   * Actualiza la sucursal preferida del usuario en el backend
   * Solo hace la petición si el usuario está autenticado
   */
  private updatePreferredStore(store: Sucursal): void {
    // Verificar si el usuario está autenticado
    const userData = localStorage.getItem('userData');
    const authToken = localStorage.getItem('authToken');

    if (!userData || !authToken) {
      return;
    }

    // Usuario autenticado - proceder con la petición al backend
    const payload: UpdateSucursalPreferidaPayload = {
      sucursalPreferida: store.id, // Enviar el ID como string
    };

    this.emailValidationService.updateSucursalPreferida(payload).subscribe({
      next: (response) => {},
      error: (error) => {
        console.error('❌ Error actualizando sucursal preferida:', error);
      },
    });
  }

  handleBackToList(): void {
    this.selectedStore = null;
  }

  openNavigation(store: Sucursal): void {
    this.selectedStoreForNavigation = store;
    this.navigationModalVisible = true;
  }

  setShowNavigationOptions(show: boolean): void {
    this.navigationModalVisible = show;
  }

  openInMaps(store: Sucursal): void {
    openInGoogleMaps(store.coordinates.lat, store.coordinates.lng);
    this.navigationModalVisible = false;
  }

  openInWaze(store: Sucursal): void {
    openInWaze(store.coordinates.lat, store.coordinates.lng, store.name);
    this.navigationModalVisible = false;
  }

  getStatusClasses(isOpen: boolean): string {
    const nowAdjusted = new Date(new Date().getTime() - 6 * 60 * 60 * 1000);

    const hour = nowAdjusted.getUTCHours();
    const day = nowAdjusted.getUTCDay();

    const isSunday = day === 0;

    const baseClass = 'text-sm font-medium fs-condensed';

    // Si es domingo, mostrar el texto de cerrado
    if (isSunday) {
      return `${baseClass} text-allasRed`;
    }

    // Si está abierto, mostrar el texto de abierto
    if (isOpen) {
      return `${baseClass} text-appPrimary-500`;
    }

    // Si está cerrado, pero es antes de las 10PM, mostrar el texto de "Contact Center"
    if (hour < 22) {
      return `${baseClass} text-orange-600`;
    }

    return `${baseClass} text-allasRed`;
  }

  getStatusText(isOpen: boolean): string {
    const nowAdjusted = new Date(new Date().getTime() - 6 * 60 * 60 * 1000);

    const hour = nowAdjusted.getUTCHours();
    const day = nowAdjusted.getUTCDay();

    const isSunday = day === 0;
    const isSaturday = day === 6;

    // Si es domingo, mostrar el texto de cerrado hasta el lunes a las 8AM
    if (isSunday) {
      return 'CERRADO - hasta el lunes a las 8AM';
    }

    // Si está abierto, mostrar el texto de abierto hasta las 5PM
    if (isOpen) {
      if (isSaturday) {
        return 'ABIERTO - hasta las 3PM';
      }
      return 'ABIERTO - hasta las 5PM';
    }

    // Si está cerrado, pero es antes de las 10PM, mostrar el texto de "Contact Center"
    if (hour < 22) {
      return 'Contact Center hasta las 10PM';
    }

    return 'CERRADO - hasta las 8AM';
  }

  getServiceIcon(iconName: string): any {
    const icons: { [key: string]: any } = {
      battery: BatteryIcon,
      droplets: DropletsIcon,
      zap: ZapIcon,
      settings: SettingsIcon,
      power: PowerIcon,
      disc: DiscIcon,
    };
    return icons[iconName] || BatteryIcon;
  }

  getDetailedHoursArray(
    detailedHours: any
  ): Array<{ day: string; hours: string; isToday: boolean }> {
    const days = [
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
      'sunday',
    ];

    return days.map((day) => {
      let hours = '8:00 a.m. - 5:00 p.m.';
      const isSunday = day === 'sunday';
      const isSaturday = day === 'saturday';

      if (isSunday) {
        hours = 'Cerrado';
      } else if (isSaturday) {
        hours = '8:00 a.m. - 3:00 p.m.';
      }

      return {
        day,
        // hours: detailedHours[day] || '8:00 AM - 8:00 PM',
        hours: hours,
        isToday: this.dayIsToday(days.indexOf(day)),
      };
    });
  }

  dayIsToday(indexDay: number): boolean {
    const today = new Date();
    const todayDay = today.getDay();

    return indexDay === todayDay;
  }

  getWeekDates(): Date[] {
    return getWeekDates();
  }

  formatDateWithDay(day: string, date: Date): string {
    return formatDateWithDay(day, date);
  }
}
